// The operator's place: /admin, the same frame as the settings (the settings
// sidebar with a way back, "Admin" opening the index, a search, the items),
// and each item's page at /admin/<id> (the waitlist, trameria cloud). The
// items come from their parts (screens/admin-waitlist.tsx,
// sandboxes/admin.tsx); this file only puts them in one place.
//
// Promise:
// - Only an operator reaches it; others are sent home and never learn it
//   exists.
// - An item's page is a settings page (title, sentence, then its own rows).
import {
  type AnyRoute,
  createRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import {
  Frame,
  SettingsIndexPage,
  SettingsPage,
  SettingsSidebar,
  useLeave,
} from "@trameria/ui-core";
import type { ComponentType } from "react";
import { HOME } from "../auth/next";
import { type Ctx, requireApproved } from "../routes";
import type { CloudSession } from "../session";

export interface AdminItem {
  /** The page is /admin/<id>. */
  id: string;
  order: number;
  label(): string;
  /** The item's mark in the sidebar (all there is of it when folded). */
  icon: ComponentType<{ className?: string }>;
  /** One sentence, under the title and in the index. */
  description(): string;
  /** Tables use the whole width; boxes of rows keep a reading width. */
  wide?: boolean;
  /** Loads what the page draws first (a failure is left to the page). */
  load?(ctx: Ctx): Promise<unknown>;
  Component: ComponentType;
}

const entries = (items: readonly AdminItem[]) =>
  items.map((item) => ({
    id: item.id,
    to: `/admin/${item.id}`,
    label: item.label,
    icon: item.icon,
    description: item.description,
  }));

function AdminSidebar({
  items,
  folded = false,
}: {
  items: readonly AdminItem[];
  folded?: boolean;
}) {
  const leave = useLeave("/admin", HOME);
  return (
    <SettingsSidebar
      label={m.web_admin_title()}
      title={m.web_admin_title()}
      indexTo="/admin"
      onBack={leave}
      groups={[{ items: entries(items) }]}
      folded={folded}
    />
  );
}

export function adminRoutes(
  root: AnyRoute,
  session: CloudSession,
  unsorted: readonly AdminItem[],
): AnyRoute[] {
  const items = [...unsorted].sort((a, b) => a.order - b.order);
  const admin = createRoute({
    getParentRoute: () => root,
    path: "admin",
    beforeLoad: async ({ context, location }) => {
      const account = await requireApproved(
        context as Ctx,
        session,
        location.href,
      );
      // Others do not learn the place exists.
      if (account.role !== "admin") throw redirect({ to: HOME });
    },
    // Folds and narrows as the settings' sidebar does; on a phone it is a
    // drawer, the same as theirs.
    component: () => (
      <Frame
        sidebar={<AdminSidebar items={items} />}
        collapsed={<AdminSidebar items={items} folded />}
        foldByWidthOnly
        label={m.web_admin_title()}
        list={false}
        menu="drawer"
      >
        <Outlet />
      </Frame>
    ),
  });
  const index = createRoute({
    getParentRoute: () => admin,
    path: "/",
    component: () => (
      <SettingsIndexPage
        title={m.web_admin_title()}
        description={m.web_admin_description()}
        groups={[{ items: entries(items) }]}
      />
    ),
  });
  const pages = items.map((item) =>
    createRoute({
      getParentRoute: () => admin,
      path: item.id,
      loader: ({ context }) => item.load?.(context as Ctx),
      component: () => (
        <SettingsPage
          title={item.label()}
          description={item.description()}
          wide={item.wide}
        >
          <item.Component />
        </SettingsPage>
      ),
    }),
  );
  return [admin.addChildren([index, ...pages])];
}
